import { LineBasicMaterial } from 'three';
import { watchImmediate } from '@vueuse/core';
import { markRaw, ref } from 'vue';
import { stop, stopAll, type StopHandle } from '@/util/stopHandle';
import type { PlannerSceneContext } from '@/views/3d-planner/scene/plannerSceneContext';
import type { PlannerStore } from '@/views/3d-planner/store/plannerStore';
import type { PlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel.types';
import type { PlannerMeshId } from '@/views/3d-planner/scene/plannerObjectId';
import { intersectionColour } from "@/views/dicoms/CatStackImageUtil";
import { updatePlaneMeshIntersection } from '@/lib/threeScene/planeMeshIntersection';
import type { PlannerCatstackPlaneId } from '@/views/3d-planner/scene/catstackImage';

/** Update catstack cross-section outlines for implants in the planner */
export function updateImplantOutlines(
    planeId: PlannerCatstackPlaneId,
    context: PlannerSceneContext,
    model: PlannerStore,
    scene: PlannerSceneViewModel,
): StopHandle {
    let update: StopHandle | null = null;

    const material = ref(
        markRaw(
            new LineBasicMaterial({
                color: intersectionColour,
                linewidth: 2,
                transparent: true,
                opacity: 0.9,
                depthTest: false,
            }),
        ),
    );

    return stopAll(
        watchImmediate(
            () => ([
                (
                    (scene.plannerMode === 'cup' || scene.plannerMode === 'stem') &&
                    model.catstacksData !== null &&
                    scene.visibility['implant-outlines'] === 'on' &&
                    scene.catstacks.rowOpen &&
                    scene.catstacks[planeId].visible
                ),
                scene.plannerMode,
            ] as const),
            ([show, mode]) => {
                stop(update);
                update = null;

                if (!show) {
                    return;
                }

                const meshId: PlannerMeshId = mode === 'cup' ? 'cup' : 'stem';
                update = updatePlaneMeshIntersection(context, planeId, planeId, meshId, material);
            },
        ),
        () => stop(update),
        // Release the LineBasicMaterial on scene teardown — without this,
        // each planner-mode rebuild leaks one per outlined plane.
        () => material.value.dispose(),
    );
}
